import { useCallback, useEffect, useState } from "react";

import { cachedUserId, generateClientId, offlineDb } from "@/lib/offline/db";
import { syncService, type SyncStatus } from "@/lib/offline/syncService";

/**
 * What actually happened to the acknowledgement:
 *   - `synced` — the server took it.
 *   - `queued` — offline, so it waits in the sync queue.
 *   - `failed` — online, but the write did not land. The sync queue logs HTTP
 *     failures and moves on rather than throwing, so callers cannot tell this
 *     from success by awaiting alone (NAU-391).
 */
export type HotHoldingAckOutcome = "synced" | "queued" | "failed";

interface UseOfflineHotHoldingAlertAckResult {
  acknowledge: () => Promise<HotHoldingAckOutcome>;
  isPending: boolean;
  syncStatus: SyncStatus;
}

/**
 * Enqueue a hot-holding alert acknowledgement that works online or offline
 * (NAU-220). The food sibling of `useOfflineIncidentSignoff`, minus the notes:
 *
 *   1. Persist a `pending` ack to `hotHoldingAlertAcks`, keyed by alertId so
 *      accidental double-taps are deduped.
 *   2. Drop the matching row from `openHotHoldingAlerts` so the dashboard rail
 *      stops showing it immediately.
 *   3. Append a `hotHoldingAlertAck` worker to the sync queue (priority 4, the
 *      same band as incident sign-off).
 *   4. If online, kick an immediate sync so the round-trip completes promptly.
 */
export function useOfflineHotHoldingAlertAck(
  alertId: string,
): UseOfflineHotHoldingAlertAckResult {
  const [isPending, setIsPending] = useState(false);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({
    pendingCount: 0,
    errorCount: 0,
    isSyncing: false,
    isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
  });

  useEffect(() => {
    syncService.getStatus().then(setSyncStatus);
    const unsubscribe = syncService.subscribe(setSyncStatus);
    return () => {
      unsubscribe();
    };
  }, []);

  const acknowledge = useCallback(async () => {
    setIsPending(true);
    try {
      await offlineDb.hotHoldingAlertAcks.put({
        id: alertId,
        queuedAt: new Date().toISOString(),
        capturedByUserId: await cachedUserId(),
        syncState: "pending",
      });

      // Optimistically remove from the open-alerts cache so the dashboard
      // updates instantly. Acknowledging something already acknowledged is a
      // no-op server-side, so a duplicate sync is harmless.
      await offlineDb.openHotHoldingAlerts.delete(alertId);

      const alreadyQueued = await offlineDb.syncQueue
        .filter(
          (i) => i.type === "hotHoldingAlertAck" && i.entityId === alertId,
        )
        .count();
      if (alreadyQueued === 0) {
        await offlineDb.syncQueue.add({
          id: generateClientId(),
          type: "hotHoldingAlertAck",
          entityId: alertId,
          priority: 4,
          createdAt: Date.now(),
          attempts: 0,
        });
      }

      if (!navigator.onLine) {
        return "queued";
      }

      await syncService.immediateSync();

      // The queue catches a failed replay, records the attempt and carries on, so
      // immediateSync resolving proves nothing. The stored record is the only
      // honest signal that the server took it.
      const ack = await offlineDb.hotHoldingAlertAcks.get(alertId);
      return ack?.syncState === "synced" ? "synced" : "failed";
    } finally {
      setIsPending(false);
    }
  }, [alertId]);

  return { acknowledge, isPending, syncStatus };
}
